/**
 * HIFLD infrastructure context for utility diligence. Coordinates, distances
 * and voltages remain useful during early screening. They cannot populate a
 * utility letter's offered capacity, firmness, connection cost or service date.
 * Actual document/authored evidence uses its own source-qualified path.
 */
import { formatFixed } from '../coerceNumber';
import type { GridData } from '../../types/siteRag';
import { inferAutoFillConfidence, type InferAutoFillConfidenceResult } from './confidenceTier';

/**
 * Public HIFLD ArcGIS Open Data — cited on every utility-letter fill.
 */
const HIFLD_GRID_URL = 'https://hifld-geoplatform.opendata.arcgis.com/datasets/electric-substations';

/**
 * `utility-letter` `authoredSchema` field keys mirrored from
 * `src/config/documentChecklists.ts`
 * (`feasibility[1].authoredSchema`). Test pins this against the live
 * schema.
 */
export const UTILITY_LETTER_FIELD_KEYS = [
  'utilityName',
  'serviceDate',
  'capacityOffered_MW',
  'interconnectionClass',
  'costEstimate_USD',
  'nearestTransmissionKm',
  'substationDistKm',
  'nearestPipelineKm',
  'nearestPipelineOperator',
  'nearestPipelineMassFlowValue',
  'nearestPipelineMassFlowUnit',
  'nearestPipelineMassFlowBasis',
] as const;

/** The enum members the dialog's `<select>` accepts. */
export type InterconnectionClass =
  | 'firm'
  | 'non-firm'
  | 'cluster'
  | 'energy-only'
  | 'network';

/** Structured-field values keyed for `AuthoredEvidenceFormState.fields`. */
export interface UtilityCoverageFill {
  fields: Partial<Record<typeof UTILITY_LETTER_FIELD_KEYS[number], string | number>>;
  notes: string;
  fieldsFilledCount: number;
  fieldsTotalCount: number;
  sourceUrl: string;
  sourceProvider: 'hifld-grid';
  confidence: InferAutoFillConfidenceResult;
}

/**
 * Map a `GridData` payload to the utility-letter coverage shape.
 *
 * @param grid  HIFLD grid provider response.
 * @param providerFreshnessDays  CAR-8 follow-up — when ≥ 90 days the
 *   confidence band is downgraded one tier (high→medium, medium→low,
 *   low→low). `GridData` does not currently carry a per-fetch
 *   timestamp on its shape, so callers that don't track freshness
 *   pass `undefined` and the helper preserves current behaviour.
 *   The richer `mapUtilityDataToCoverage` (in
 *   `services/dataProviders/utility/utilityCoverageMapper.ts`) has
 *   its own age-aware confidence pipeline.
 */
export function mapGridDataToCoverage(
  grid: GridData,
  providerFreshnessDays?: number,
): UtilityCoverageFill {
  const fields: UtilityCoverageFill['fields'] = {};
  const filled = 0;
  const notes = buildUtilityNotes(grid);

  const confidence = inferAutoFillConfidence({
    fillSource: 'provider',
    fieldsFilledCount: filled,
    fieldsTotalCount: UTILITY_LETTER_FIELD_KEYS.length,
    // CAR-8 follow-up: forward freshness when finite; NaN / ±Infinity
    // collapse to undefined so the cap check stays a clean numeric compare.
    providerFreshnessDays:
      Number.isFinite(providerFreshnessDays) ? providerFreshnessDays : undefined,
  });

  return {
    fields,
    notes,
    fieldsFilledCount: filled,
    fieldsTotalCount: UTILITY_LETTER_FIELD_KEYS.length,
    sourceUrl: HIFLD_GRID_URL,
    sourceProvider: 'hifld-grid',
    confidence,
  };
}

/**
 * Parse the `GridData.voltage` string (`'345kV'`, `'230kV'`, `'69kV'`,
 * `'Unknown'`, free-form) to an integer kilovolt value. Returns null
 * when the string is unrecognised — we'd rather emit no value than
 * default a misleading "115kV".
 */
export function parseVoltageKv(voltage: string | undefined | null): number | null {
  if (!voltage || typeof voltage !== 'string') return null;
  const m = voltage.trim().match(/^(\d+(?:\.\d+)?)\s*kV$/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Map substation voltage (kV) to typical interconnection class per
 * pack §CAR-2 table. Conservative midpoint of each band:
 *   - ≥345kV → firm (bulk transmission)
 *   - 138-230kV → firm  (high end of band; conservative)
 *   - 69-115kV → non-firm
 *   - <69kV → energy-only
 *
 * Note: the pack lists alternatives ("firm OR non-firm depending on
 * ATC") for the middle bands — without ATC data we pick the more
 * conservative single value per band. ATC integration is
 * CAR-FOLLOWUP-UTILITY-ATC.
 */
export function inferInterconnectionClass(voltageKv: number): InterconnectionClass {
  if (voltageKv >= 345) return 'firm';
  if (voltageKv >= 138) return 'firm';
  if (voltageKv >= 69) return 'non-firm';
  return 'energy-only';
}

/**
 * Capacity proxy in MW from substation voltage. Conservative
 * midpoints derived from regional substation typicals — clearly
 * flagged in notes as a PROXY (a real interconnection study refines).
 * Pack §CAR-2 calls this the "substation capacity proxy from HIFLD".
 */
export function inferCapacityProxyMw(voltageKv: number): number | null {
  if (voltageKv >= 500) return 1000;
  if (voltageKv >= 345) return 450;
  if (voltageKv >= 230) return 200;
  if (voltageKv >= 138) return 100;
  if (voltageKv >= 115) return 60;
  if (voltageKv >= 69) return 30;
  return null;
}

/**
 * Parse the HIFLD provider's pre-computed `estimatedInterconnectionCost`
 * range string (`'$1-3M'`, `'$3-8M'`, `'$8-20M'`, `'$15-40M+'`) and
 * return the midpoint USD value. Returns null for unrecognised inputs.
 *
 * Conservative: the `'$15-40M+'` band is treated as midpoint $27.5M
 * (no extra "+" margin — we don't speculate beyond the upper bound).
 */
export function parseCostBandToMidpointUsd(band: string | undefined | null): number | null {
  if (!band || typeof band !== 'string') return null;
  const m = band.match(/^\$(\d+(?:\.\d+)?)[-–](\d+(?:\.\d+)?)M/);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[2]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  const midM = (lo + hi) / 2;
  return Math.round(midM * 1_000_000);
}

/**
 * Build the `notes` string. Always frames the fill as auto-filled,
 * always names HIFLD as the source, always recommends a real
 * interconnection study.
 */
function buildUtilityNotes(grid: GridData): string {
  const parts = ['Infrastructure context from HIFLD Electric Substations + Transmission Lines.'];
  if (grid.nearestSubstationName) {
    parts.push(`Nearest substation: ${grid.nearestSubstationName} (${grid.voltage}, ${formatFixed(grid.substationDist, 1, '?')}km).`);
  }
  if (grid.substationsWithin50km != null) parts.push(`${grid.substationsWithin50km} substation(s) within 50km.`);
  if (grid.nearestTransmissionVoltage) parts.push(`Nearest transmission line: ${grid.nearestTransmissionVoltage}.`);
  if (grid.estimatedInterconnectionCost) {
    parts.push(`Legacy distance-based cost heuristic: ${grid.estimatedInterconnectionCost}; unverified context, not a site cost estimate or utility offer.`);
  }
  if (grid.estimatedQueueTime) {
    parts.push(`Duration context: ${grid.estimatedQueueTime}; not a confirmed site load-service timeline.`);
  }
  parts.push('Offered MW, firm service class, utility connection cost and service date remain unknown without a site-specific utility letter or study.');
  parts.push('Utility identity or nearby grid infrastructure alone does not establish these commitments.');
  return parts.join(' ');
}
